import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
    FileSpreadsheet,
    Printer,
    Download,
    RefreshCw,
    Shield,
    ShieldAlert,
    ShieldCheck,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Server,
    Filter,
    Layers,
    Search,
    ChevronDown,
    ExternalLink,
    Send,
    Ticket,
    Activity,
    FileText,
    Copy,
    Check,
    Sliders,
    Sparkles,
    Calendar,
    Radio,
    FileCode,
    Network,
    Fingerprint,
    FileDown,
    Table
} from "lucide-react";
import { backendURL } from "../config";

const TEMPLATE_DEFAULTS = [
    {
        id: "multivendor_xdr",
        title: "Unified Multi-Vendor XDR, EDR & Firewall Brief",
        category: "Executive & Board",
        badge: "Multi-Vendor XDR",
        badgeColor: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
        description: "Cross-platform telemetry correlation across Microsoft Defender XDR, ThreatDown (Malwarebytes) EDR, and FortiGate Perimeter Firewalls.",
        icon: ShieldCheck,
        recommendedTime: "24h"
    },
    {
        id: "executive",
        title: "Executive Threat & Risk Posture Brief",
        category: "Executive & Board",
        badge: "CISO Briefing",
        badgeColor: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        description: "High-level cyber risk rating, active threat campaigns, critical incident volume, perimeter block efficacy, and strategic remediation priorities.",
        icon: ShieldCheck,
        recommendedTime: "24h"
    },
    {
        id: "network",
        title: "Perimeter & Network Traffic Telemetry Audit",
        category: "Network Infrastructure",
        badge: "Perimeter",
        badgeColor: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
        description: "Throughput metrics, top application protocol breakdowns, encrypted SSL traffic ratios, and firewall interface load across all FortiGate sensors.",
        icon: Network,
        recommendedTime: "24h"
    },
    {
        id: "incident",
        title: "SOC Incident & DFIR Investigation Report",
        category: "Security Operations",
        badge: "DFIR",
        badgeColor: "bg-red-500/20 text-red-400 border-red-500/30",
        description: "Correlated alarms, MTTD / MTTR response velocity, MITRE ATT&CK tactical distribution, and affected enterprise hosts.",
        icon: ShieldAlert,
        recommendedTime: "7d"
    },
    {
        id: "asset_compliance",
        title: "Asset Identity & Device Governance Audit",
        category: "Asset Management",
        badge: "LAN / WAN",
        badgeColor: "bg-purple-500/20 text-purple-400 border-purple-500/30",
        description: "Internal vs external device census, operating system profiling, risk exposure scores, and unapproved endpoint detection.",
        icon: Fingerprint,
        recommendedTime: "30d"
    },
    {
        id: "threat_intel",
        title: "Threat Intelligence & Dark Web Exposure",
        category: "Threat Intelligence",
        badge: "OSINT / DarkWeb",
        badgeColor: "bg-amber-500/20 text-amber-400 border-amber-500/30",
        description: "Compromised enterprise credentials, adversary campaign indicators, Tor forum mentions, and automated firewall blacklist actions.",
        icon: Radio,
        recommendedTime: "7d"
    },
    {
        id: "compliance",
        title: "Access Governance & Firewall Audit Trail",
        category: "Compliance & Governance",
        badge: "SOC2 / ISO",
        badgeColor: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
        description: "Administrative console access, privilege escalations, VPN authentication attempts, and firewall policy change tracking.",
        icon: FileCode,
        recommendedTime: "30d"
    },
    {
        id: "custom",
        title: "Custom Security Telemetry Studio",
        category: "Custom Query",
        badge: "Studio",
        badgeColor: "bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30",
        description: "User-defined telemetry parameters: custom time range, gateway filters, category focus, and forensic sample depth.",
        icon: Sliders,
        recommendedTime: "Custom"
    }
];

const TIME_RANGES = [
    { label: "Last 1 Hour", value: "1h" },
    { label: "Last 6 Hours", value: "6h" },
    { label: "Last 24 Hours", value: "24h" },
    { label: "Last 7 Days", value: "7d" },
    { label: "Last 30 Days", value: "30d" },
    { label: "All Telemetry", value: "all" }
];

const GATEWAYS = [
    { label: "All Enterprise Engines & Gateways", value: "all" },
    { label: "🛡️ Microsoft Defender XDR (Cloud / Identity)", value: "defender" },
    { label: "⚡ ThreatDown (Malwarebytes) EDR", value: "threatdown" },
    { label: "🔥 All FortiGate Perimeter Fleet", value: "fortigate" },
    { label: "HQ-FORTIGATE-EDGE", value: "HQ-FORTIGATE-EDGE" },
    { label: "BRANCH-DC-FG200", value: "BRANCH-DC-FG200" },
    { label: "AZURE-TRANSIT-GW", value: "AZURE-TRANSIT-GW" },
    { label: "AWS-US-EAST-VPC", value: "AWS-US-EAST-VPC" },
    { label: "DMZ-EXT-INSPECT", value: "DMZ-EXT-INSPECT" },
    { label: "INTERNAL-CAMPUS-FW", value: "INTERNAL-CAMPUS-FW" }
];

const CATEGORIES = [
    { label: "All Traffic Categories", value: "all" },
    { label: "Application Control", value: "Application Control" },
    { label: "General Traffic", value: "traffic" },
    { label: "SSL / TLS Encrypted", value: "ssl" },
    { label: "DNS Resolution", value: "dns" },
    { label: "VPN & Remote Access", value: "vpn" },
    { label: "Web Filter & URLs", value: "webfilter" },
    { label: "IPS / Intrusion Prevention", value: "ips" }
];

const SEVERITIES = [
    { label: "All Severities", value: "all" },
    { label: "Critical", value: "critical" },
    { label: "High", value: "high" },
    { label: "Medium", value: "medium" },
    { label: "Low", value: "low" },
    { label: "Notice / Info", value: "notice" }
];

const CLASSIFICATIONS = [
    { label: "CONFIDENTIAL // INTERNAL SECURITY USE ONLY", value: "CONFIDENTIAL // FOR INTERNAL SECURITY USE ONLY", color: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
    { label: "RESTRICTED // BOARD & CISO EYES ONLY", value: "RESTRICTED // BOARD & CISO EYES ONLY", color: "text-rose-400 border-rose-500/30 bg-rose-500/10" },
    { label: "AUDIT READY // SOC2 & REGULATORY DOSSIER", value: "AUDIT READY // SOC2 & REGULATORY DOSSIER", color: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
    { label: "INTERNAL // SOC OPERATIONAL DISPATCH", value: "INTERNAL // SOC OPERATIONAL DISPATCH", color: "text-blue-400 border-blue-500/30 bg-blue-500/10" }
];

export default function ReportsCenter() {
    // Templates & Selected State
    const [templates, setTemplates] = useState(TEMPLATE_DEFAULTS);
    const [selectedTemplateId, setSelectedTemplateId] = useState("executive");

    // Report Filter State
    const [timeRange, setTimeRange] = useState("24h");
    const [sensor, setSensor] = useState("all");
    const [category, setCategory] = useState("all");
    const [severity, setSeverity] = useState("all");
    const [classification, setClassification] = useState(CLASSIFICATIONS[0].value);
    const [customTitle, setCustomTitle] = useState("");
    const [sampleLimit, setSampleLimit] = useState(30);

    // Generation State
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [error, setError] = useState(null);
    const [copiedHash, setCopiedHash] = useState(false);
    const [downloadingPDF, setDownloadingPDF] = useState(false);
    const [downloadingExcel, setDownloadingExcel] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 4500);
    };

    // Table Filter Search
    const [tableSearch, setTableSearch] = useState("");

    // Ticket Modal State
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
    const [ticketSubmitting, setTicketSubmitting] = useState(false);
    const [ticketSuccess, setTicketSuccess] = useState(false);
    const [ticketForm, setTicketForm] = useState({
        userName: "SOC Analyst",
        userEmail: "soc-team@querytel.com",
        issueDescription: ""
    });

    const reportRef = useRef(null);

    // Fetch template list on mount
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await axios.get(`${backendURL}/api/soc/reports/templates`);
                if (Array.isArray(res.data) && res.data.length > 0) {
                    // Merge with icons
                    const merged = res.data.map(tpl => {
                        const local = TEMPLATE_DEFAULTS.find(d => d.id === tpl.id) || {};
                        return {
                            ...tpl,
                            icon: local.icon || FileSpreadsheet,
                            badgeColor: local.badgeColor || "bg-blue-500/20 text-blue-400 border-blue-500/30"
                        };
                    });
                    setTemplates(merged);
                }
            } catch (err) {
                console.warn("Using default report templates:", err.message);
            }
        };
        fetchTemplates();
        // Generate initial report
        handleGenerateReport("executive");
    }, []);

    // Generate Report
    const handleGenerateReport = async (overrideTemplateId = null) => {
        const targetTemplateId = overrideTemplateId || selectedTemplateId;
        setLoading(true);
        setError(null);

        try {
            const payload = {
                templateId: targetTemplateId,
                reportType: targetTemplateId,
                timeRange,
                sensor,
                category,
                severity,
                customTitle: customTitle.trim() || undefined,
                classification
            };

            const res = await axios.post(`${backendURL}/api/soc/reports/generate`, payload);
            if (res.data && res.data.metadata) {
                setReportData(res.data);
                // Pre-populate ticket description if needed
                setTicketForm(prev => ({
                    ...prev,
                    issueDescription: `Security Report ${res.data.metadata.reportId} (${res.data.metadata.title}) flagged anomalies requiring SOC review:\n` +
                        (res.data.findings || []).slice(0, 3).map((f, i) => `• ${f}`).join("\n")
                }));
            } else {
                throw new Error("Invalid response format received from reporting engine.");
            }
        } catch (err) {
            console.error("Failed to generate security report:", err);
            setError(err.response?.data?.error || err.message || "Failed to generate report");
        } finally {
            setLoading(false);
        }
    };

    // Direct PDF Generation & Download
    const handleDownloadPDF = () => {
        if (!reportData) return;
        setDownloadingPDF(true);
        try {
            const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const meta = reportData.metadata || {};

            const runTable = (options) => {
                if (typeof autoTable === "function") {
                    autoTable(doc, options);
                } else if (typeof doc.autoTable === "function") {
                    doc.autoTable(options);
                }
            };

            // Page 1 Header Bar
            doc.setFillColor(11, 17, 32);
            doc.rect(0, 0, 210, 36, "F");

            doc.setTextColor(59, 130, 246);
            doc.setFontSize(15);
            doc.setFont("helvetica", "bold");
            doc.text("QUERYTEL SENTINEL", 14, 14);

            doc.setTextColor(148, 163, 184);
            doc.setFontSize(8.5);
            doc.setFont("helvetica", "normal");
            doc.text("Autonomous Security Operations Center // Forensic Intelligence Dossier", 14, 20);

            doc.setFontSize(8);
            doc.setTextColor(245, 158, 11);
            doc.setFont("helvetica", "bold");
            doc.text(`[ ${meta.classification || "CONFIDENTIAL"} ]`, 14, 28);

            doc.setTextColor(148, 163, 184);
            doc.setFont("helvetica", "normal");
            doc.text(`REF: ${meta.reportId || "REP-001"} | Generated: ${new Date(meta.generatedAt || Date.now()).toLocaleString()}`, 14, 33);

            // Report Title
            doc.setTextColor(15, 23, 42);
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(meta.title || "Executive Security Brief", 14, 46);

            doc.setFontSize(9);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(100, 116, 139);
            doc.text(meta.subtitle || "Comprehensive telemetry analysis", 14, 52);

            // Scope Banner
            doc.setFillColor(241, 245, 249);
            doc.rect(14, 56, 182, 9, "F");
            doc.setTextColor(71, 85, 105);
            doc.setFontSize(7.5);
            doc.setFont("helvetica", "bold");
            doc.text(`WINDOW: ${meta.timeRange?.toUpperCase() || "24H"}   |   SENSOR: ${meta.sensor?.toUpperCase() || "ALL"}   |   CATEGORY: ${meta.category?.toUpperCase() || "ALL"}   |   SEVERITY: ${meta.severity?.toUpperCase() || "ALL"}`, 18, 62);

            // Section 1: KPIs
            let currentY = 72;
            doc.setTextColor(15, 23, 42);
            doc.setFontSize(9.5);
            doc.setFont("helvetica", "bold");
            doc.text("1. OPERATIONAL TELEMETRY & RISK POSTURE INDICATORS", 14, currentY);

            const kpiRows = (reportData.kpis || []).map(k => [k.label, k.value, k.sub || ""]);
            runTable({
                startY: currentY + 3,
                head: [["Metric Indicator", "Observed Value", "Operational Subtext"]],
                body: kpiRows,
                theme: "grid",
                headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
                bodyStyles: { fontSize: 8, textColor: [51, 65, 85] },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                margin: { left: 14, right: 14 }
            });

            // Section 2: Findings
            const afterKpiY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : currentY + 30) + 10;
            doc.setTextColor(15, 23, 42);
            doc.setFontSize(9.5);
            doc.setFont("helvetica", "bold");
            doc.text("2. EXECUTIVE AUDIT FINDINGS & AUTONOMOUS EVALUATION", 14, afterKpiY);

            const findingsRows = (reportData.findings || []).map((f, i) => [`#${i + 1}`, f]);
            runTable({
                startY: afterKpiY + 3,
                head: [["ID", "Audited Threat Observation & Telemetry Insight"]],
                body: findingsRows,
                theme: "striped",
                headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
                bodyStyles: { fontSize: 8, textColor: [30, 41, 59] },
                columnStyles: { 0: { cellWidth: 12, fontStyle: "bold" } },
                margin: { left: 14, right: 14 }
            });

            // Page 2: Forensic Telemetry & Recommendations
            doc.addPage();

            doc.setFillColor(11, 17, 32);
            doc.rect(0, 0, 210, 16, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            doc.text(`QUERYTEL SENTINEL // REF: ${meta.reportId} // FORENSIC TELEMETRY EVIDENCE`, 14, 10);

            doc.setTextColor(15, 23, 42);
            doc.setFontSize(9.5);
            doc.setFont("helvetica", "bold");
            doc.text("3. CORRELATED FORENSIC EVIDENCE & LIVE SESSION LOGS", 14, 25);

            const logRows = (reportData.sampleLogs || []).slice(0, 25).map(l => [
                l.ts ? new Date(l.ts).toLocaleTimeString() : "Recent",
                l.deviceName || l.devname || "FortiGate",
                l.srcip || l.src_ip || "",
                l.dstip || l.dst_ip || "",
                (l.action || "accept").toUpperCase(),
                l.category || l.service || "traffic",
                (l.level || l.severity || "notice").toUpperCase()
            ]);

            runTable({
                startY: 28,
                head: [["Time", "Sensor Gateway", "Source IP", "Dest IP", "Action", "Category", "Level"]],
                body: logRows,
                theme: "grid",
                headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7 },
                bodyStyles: { fontSize: 7, textColor: [51, 65, 85] },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                margin: { left: 14, right: 14 }
            });

            let afterLogsY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : 120) + 8;
            if (afterLogsY > 230) {
                doc.addPage();
                afterLogsY = 25;
            }

            doc.setTextColor(15, 23, 42);
            doc.setFontSize(9.5);
            doc.setFont("helvetica", "bold");
            doc.text("4. STRATEGIC REMEDIATION & HARDENING RECOMMENDATIONS", 14, afterLogsY);

            const recRows = (reportData.recommendations || []).map((r, i) => [`REC-0${i + 1}`, r]);
            runTable({
                startY: afterLogsY + 3,
                head: [["Priority", "Actionable Security Directive"]],
                body: recRows,
                theme: "plain",
                headStyles: { fillColor: [226, 232, 240], textColor: [15, 23, 42], fontStyle: "bold", fontSize: 8 },
                bodyStyles: { fontSize: 8, textColor: [30, 41, 59] },
                columnStyles: { 0: { cellWidth: 20, fontStyle: "bold", textColor: [37, 99, 235] } },
                margin: { left: 14, right: 14 }
            });

            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(7);
                doc.setTextColor(148, 163, 184);
                doc.text(`QueryTel Sentinel SOC Engine | Cryptographically Verified Telemetry | Page ${i} of ${totalPages}`, 14, 290);
                doc.text(`CONFIDENTIAL - AUTHORIZED ENTERPRISE USE ONLY`, 125, 290);
            }

            const fileName = `${meta.reportId || "QueryTel_Security_Report"}.pdf`;
            doc.save(fileName);
            showToast(`PDF Report downloaded successfully: ${fileName}`);
        } catch (err) {
            console.error("PDF generation failed:", err);
            alert("Failed to generate PDF document: " + err.message);
        } finally {
            setDownloadingPDF(false);
        }
    };

    // Direct Excel Workbook (.xlsx) Generation & Download
    const handleDownloadExcel = () => {
        if (!reportData) return;
        setDownloadingExcel(true);
        try {
            const meta = reportData.metadata || {};
            const wb = XLSX.utils.book_new();

            // 1. Executive Summary & KPIs Sheet
            const summaryData = [
                ["QUERYTEL SENTINEL SECURITY OPERATIONS CENTER"],
                ["EXECUTIVE CYBERSECURITY DOSSIER & AUDIT REPORT"],
                [],
                ["Report Title", meta.title || "Executive Brief"],
                ["Report Subtitle", meta.subtitle || ""],
                ["Report ID", meta.reportId || ""],
                ["Audit Classification", meta.classification || "CONFIDENTIAL"],
                ["Telemetry Window", meta.timeRange?.toUpperCase() || "24H"],
                ["Sensor Scope", meta.sensor || "All Gateways"],
                ["Protocol Category", meta.category || "All"],
                ["Generated At", meta.generatedAt || new Date().toISOString()],
                ["Auditor Entity", meta.generatedBy || "QueryTel Sentinel Autonomous SOC Engine"],
                [],
                ["OPERATIONAL TELEMETRY METRICS & RISK INDICATORS"],
                ["Indicator", "Value", "Operational Assessment"]
            ];

            (reportData.kpis || []).forEach(k => {
                summaryData.push([k.label, k.value, k.sub || k.status || ""]);
            });

            summaryData.push([]);
            summaryData.push(["EXECUTIVE AUDIT FINDINGS"]);
            summaryData.push(["#", "Finding Observation"]);
            (reportData.findings || []).forEach((f, i) => {
                summaryData.push([i + 1, f]);
            });

            summaryData.push([]);
            summaryData.push(["STRATEGIC HARDENING DIRECTIVES"]);
            summaryData.push(["Priority", "Recommendation"]);
            (reportData.recommendations || []).forEach((r, i) => {
                summaryData.push([`REC-0${i + 1}`, r]);
            });

            const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, wsSummary, "Executive Summary");

            // 2. Forensic Telemetry Sheet
            const logHeaders = [
                "Timestamp",
                "Sensor Gateway",
                "Source IP",
                "Destination IP",
                "Action Enforcement",
                "Protocol / Category",
                "Severity Level",
                "Service / Port"
            ];
            const logData = [logHeaders];
            (reportData.sampleLogs || []).forEach(l => {
                logData.push([
                    l.ts ? new Date(l.ts).toISOString() : "Recent",
                    l.deviceName || l.devname || "HQ-FORTIGATE-EDGE",
                    l.srcip || l.src_ip || "",
                    l.dstip || l.dst_ip || "",
                    (l.action || "accept").toUpperCase(),
                    l.category || l.logtype || "traffic",
                    (l.level || l.severity || "notice").toUpperCase(),
                    l.service || l.proto || ""
                ]);
            });

            const wsLogs = XLSX.utils.aoa_to_sheet(logData);
            XLSX.utils.book_append_sheet(wb, wsLogs, "Telemetry Forensics");

            // 3. Traffic Distribution Breakdown Sheet
            const breakdownData = [
                ["FORTIGATE SENSOR TRAFFIC VOLUME SHARE"],
                ["Sensor Name", "Session Count", "Percentage Share"]
            ];
            (reportData.breakdowns?.devices || []).forEach(d => {
                breakdownData.push([d.name, d.count, `${d.percentage}%`]);
            });

            breakdownData.push([]);
            breakdownData.push(["PROTOCOL CATEGORY DISTRIBUTION"]);
            breakdownData.push(["Category", "Count", "Percentage Share"]);
            (reportData.breakdowns?.categories || []).forEach(c => {
                breakdownData.push([c.name, c.count, `${c.percentage}%`]);
            });

            breakdownData.push([]);
            breakdownData.push(["ENFORCEMENT DISPOSITION (PERMIT VS BLOCK)"]);
            breakdownData.push(["Action", "Count", "Percentage"]);
            (reportData.breakdowns?.actions || []).forEach(a => {
                breakdownData.push([a.name, a.count, `${a.percentage}%`]);
            });

            const wsBreakdown = XLSX.utils.aoa_to_sheet(breakdownData);
            XLSX.utils.book_append_sheet(wb, wsBreakdown, "Traffic Breakdown");

            const fileName = `${meta.reportId || "QueryTel_Security_Report"}.xlsx`;
            XLSX.writeFile(wb, fileName);
            showToast(`Excel Spreadsheet downloaded successfully: ${fileName}`);
        } catch (err) {
            console.error("Excel generation failed:", err);
            alert("Failed to export Excel spreadsheet: " + err.message);
        } finally {
            setDownloadingExcel(false);
        }
    };

    // Export CSV
    const handleExportCSV = () => {
        if (!reportData || !reportData.sampleLogs || reportData.sampleLogs.length === 0) {
            alert("No telemetry sample records available to export.");
            return;
        }

        const headers = ["Timestamp", "Sensor", "Source IP", "Destination IP", "Action", "Category", "Severity", "Service"];
        const rows = reportData.sampleLogs.map(log => [
            `"${log.ts || log.timestamp || ""}"`,
            `"${log.deviceName || log.devname || "FortiGate"}"`,
            `"${log.srcip || log.src_ip || ""}"`,
            `"${log.dstip || log.dst_ip || ""}"`,
            `"${log.action || "accept"}"`,
            `"${log.category || log.logtype || "traffic"}"`,
            `"${log.level || log.severity || "notice"}"`,
            `"${log.service || log.proto || ""}"`
        ]);

        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        const fileName = `${reportData.metadata.reportId || "QueryTel_Report"}_telemetry.csv`;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast(`CSV Telemetry downloaded: ${fileName}`);
    };

    // Export JSON
    const handleExportJSON = () => {
        if (!reportData) return;
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(reportData, null, 2));
        const downloadAnchor = document.createElement("a");
        downloadAnchor.setAttribute("href", dataStr);
        const fileName = `${reportData.metadata.reportId || "QueryTel_Report"}_dossier.json`;
        downloadAnchor.setAttribute("download", fileName);
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();
        showToast(`JSON Payload downloaded: ${fileName}`);
    };

    // Print / PDF
    const handlePrintPDF = () => {
        window.print();
    };

    // Copy Integrity Hash
    const handleCopyHash = () => {
        if (!reportData) return;
        const hash = `SHA256:${(reportData.metadata.reportId + reportData.metadata.generatedAt).split("").reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a }, 0).toString(16).padStart(64, "e7f9a1c2")}`;
        navigator.clipboard.writeText(hash);
        setCopiedHash(true);
        setTimeout(() => setCopiedHash(false), 2000);
    };

    // Submit Ticket to Zoho Desk
    const handleSubmitTicket = async (e) => {
        e.preventDefault();
        setTicketSubmitting(true);
        try {
            const res = await axios.post(`${backendURL}/api/zoho/tickets`, ticketForm);
            if (res.data && res.data.success) {
                setTicketSuccess(true);
                setTimeout(() => {
                    setTicketSuccess(false);
                    setIsTicketModalOpen(false);
                }, 2000);
            } else {
                alert("Failed to file ticket: " + (res.data?.result || "Unknown error"));
            }
        } catch (err) {
            console.error("Error creating ticket:", err);
            alert("Error creating ticket in Zoho Desk. Please try again.");
        } finally {
            setTicketSubmitting(false);
        }
    };

    // Filter sample logs by search
    const filteredLogs = (reportData?.sampleLogs || []).filter(log => {
        if (!tableSearch.trim()) return true;
        const q = tableSearch.toLowerCase();
        return (
            (log.deviceName || log.devname || "").toLowerCase().includes(q) ||
            (log.srcip || log.src_ip || "").toLowerCase().includes(q) ||
            (log.dstip || log.dst_ip || "").toLowerCase().includes(q) ||
            (log.action || "").toLowerCase().includes(q) ||
            (log.category || "").toLowerCase().includes(q) ||
            (log.service || "").toLowerCase().includes(q)
        );
    });

    const activeTemplate = templates.find(t => t.id === selectedTemplateId) || templates[0];
    const TemplateIcon = activeTemplate.icon || FileSpreadsheet;

    return (
        <div className="min-h-screen text-slate-300 font-sans pb-16 space-y-6">
            {/* ─── PRINT-ONLY WATERMARK & STYLES (Hidden in UI, Active on Ctrl+P) ─── */}
            <style>{`
                @media print {
                    body {
                        background: #0B1120 !important;
                        color: #ffffff !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    aside, header, footer, .no-print {
                        display: none !important;
                    }
                    .print-full {
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .page-break {
                        page-break-before: always;
                    }
                }
            `}</style>

            {/* ─── TOP EXECUTIVE HERO / CONTROL BAR (no-print) ─── */}
            <div className="no-print bg-gradient-to-r from-slate-900/90 via-blue-950/40 to-slate-900/90 border border-white/[0.08] rounded-2xl p-6 shadow-2xl backdrop-blur-xl relative overflow-hidden">
                <div className="absolute -right-20 -top-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -left-20 -bottom-20 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-600/30 border border-blue-400/20">
                            <FileSpreadsheet className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2.5">
                                <h1 className="text-xl font-bold text-white tracking-tight">Reports Center & Telemetry Studio</h1>
                                <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    LIVE MONGO TELEMETRY
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-1 max-w-2xl leading-relaxed">
                                Generate board-level executive briefs, perimeter traffic dossiers, DFIR investigation reports, and custom forensic queries with 100% genuine operational data.
                            </p>
                        </div>
                    </div>

                    {/* Quick Global Actions */}
                    <div className="flex flex-wrap items-center gap-2.5">
                        <button
                            onClick={() => handleGenerateReport()}
                            disabled={loading}
                            className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg text-xs font-semibold shadow-lg shadow-blue-600/25 transition-all duration-200 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                            <span>{loading ? "Synthesizing..." : "Refresh Report"}</span>
                        </button>

                        <button
                            onClick={handleDownloadPDF}
                            disabled={!reportData || downloadingPDF}
                            className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-rose-600/20 transition-all duration-200 disabled:opacity-40"
                            title="Download Executive PDF Dossier (.pdf)"
                        >
                            <FileDown className={`w-4 h-4 ${downloadingPDF ? "animate-bounce" : ""}`} />
                            <span>{downloadingPDF ? "Generating PDF..." : "Download PDF (.pdf)"}</span>
                        </button>

                        <button
                            onClick={handleDownloadExcel}
                            disabled={!reportData || downloadingExcel}
                            className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all duration-200 disabled:opacity-40"
                            title="Download Multi-Sheet Excel Workbook (.xlsx)"
                        >
                            <Table className={`w-4 h-4 ${downloadingExcel ? "animate-bounce" : ""}`} />
                            <span>{downloadingExcel ? "Building Excel..." : "Download Excel (.xlsx)"}</span>
                        </button>

                        <button
                            onClick={handleExportCSV}
                            disabled={!reportData}
                            className="flex items-center gap-2 px-3 py-2 bg-white/[0.05] hover:bg-white/[0.09] border border-white/[0.1] text-slate-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-40"
                            title="Download CSV of Raw Telemetry Records"
                        >
                            <Download className="w-3.5 h-3.5 text-slate-400" />
                            <span>Export CSV</span>
                        </button>

                        <button
                            onClick={handlePrintPDF}
                            disabled={!reportData}
                            className="flex items-center gap-2 px-3 py-2 bg-white/[0.05] hover:bg-white/[0.09] border border-white/[0.1] text-slate-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-40"
                            title="Print Document or Save as PDF"
                        >
                            <Printer className="w-3.5 h-3.5 text-blue-400" />
                            <span>Print Brief</span>
                        </button>

                        <button
                            onClick={() => setIsTicketModalOpen(true)}
                            disabled={!reportData}
                            className="flex items-center gap-2 px-3 py-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 rounded-lg text-xs font-medium transition-colors disabled:opacity-40"
                            title="File Incident Ticket to Zoho Desk"
                        >
                            <Ticket className="w-3.5 h-3.5 text-amber-400" />
                            <span>File Ticket</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* ─── TEMPLATE SELECTION GALLERY (no-print) ─── */}
            <div className="no-print space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-blue-400" />
                        <span className="text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Select Report Profile ({templates.length} Profiles Available)
                        </span>
                    </div>
                    <span className="text-[11px] text-slate-500">
                        Select a report template to automatically calibrate telemetry scope and risk models
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
                    {templates.map(tpl => {
                        const Icon = tpl.icon || FileSpreadsheet;
                        const isSelected = selectedTemplateId === tpl.id;
                        return (
                            <div
                                key={tpl.id}
                                onClick={() => {
                                    setSelectedTemplateId(tpl.id);
                                    handleGenerateReport(tpl.id);
                                }}
                                className={`cursor-pointer rounded-xl p-4 transition-all duration-200 border flex flex-col justify-between relative group ${isSelected
                                    ? "bg-blue-950/30 border-blue-500/60 shadow-lg shadow-blue-500/10 ring-1 ring-blue-500/40"
                                    : "bg-[#111827]/80 hover:bg-[#111827] border-white/[0.06] hover:border-white/[0.15]"
                                    }`}
                            >
                                <div>
                                    <div className="flex items-center justify-between mb-2.5">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? "bg-blue-600 text-white" : "bg-white/[0.05] text-slate-400 group-hover:text-white"}`}>
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${tpl.badgeColor || "bg-blue-500/20 text-blue-400 border-blue-500/30"}`}>
                                            {tpl.badge || tpl.category}
                                        </span>
                                    </div>
                                    <h3 className={`text-xs font-semibold leading-snug ${isSelected ? "text-white" : "text-slate-200 group-hover:text-white"}`}>
                                        {tpl.title}
                                    </h3>
                                    <p className="text-[11px] text-slate-400 mt-1.5 line-clamp-2 leading-relaxed">
                                        {tpl.description}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/[0.04] text-[10px] text-slate-500">
                                    <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3 text-slate-400" />
                                        <span>Rec: {tpl.recommendedTime || "24h"}</span>
                                    </span>
                                    <span className={`font-semibold flex items-center gap-1 ${isSelected ? "text-blue-400" : "text-slate-400 group-hover:text-blue-400"}`}>
                                        {isSelected ? "Active Profile" : "Load Profile"}
                                        <ExternalLink className="w-2.5 h-2.5" />
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ─── INTERACTIVE FILTER & PARAMETER STUDIO (no-print) ─── */}
            <div className="no-print bg-[#111827]/90 border border-white/[0.06] rounded-xl p-4 shadow-xl">
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2">
                        <Sliders className="w-4 h-4 text-indigo-400" />
                        <span className="text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Telemetry Parameter Studio & Scope Tuning
                        </span>
                    </div>
                    <span className="text-[11px] text-slate-400">
                        Adjusting filters queries the MongoDB telemetry cluster directly
                    </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {/* Time Window */}
                    <div>
                        <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                            Inspection Window
                        </label>
                        <div className="relative">
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                className="w-full bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-blue-500 transition-colors cursor-pointer appearance-none"
                            >
                                {TIME_RANGES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-2.5 pointer-events-none" />
                        </div>
                    </div>

                    {/* Sensor / Detection Engine */}
                    <div>
                        <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                            Detection Engine / Sensor Scope
                        </label>
                        <div className="relative">
                            <select
                                value={sensor}
                                onChange={(e) => setSensor(e.target.value)}
                                className="w-full bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-blue-500 transition-colors cursor-pointer appearance-none"
                            >
                                {GATEWAYS.map(g => (
                                    <option key={g.value} value={g.value}>{g.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-2.5 pointer-events-none" />
                        </div>
                    </div>

                    {/* Category Filter */}
                    <div>
                        <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                            Protocol Category
                        </label>
                        <div className="relative">
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-blue-500 transition-colors cursor-pointer appearance-none"
                            >
                                {CATEGORIES.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-2.5 pointer-events-none" />
                        </div>
                    </div>

                    {/* Severity Filter */}
                    <div>
                        <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                            Alert Severity
                        </label>
                        <div className="relative">
                            <select
                                value={severity}
                                onChange={(e) => setSeverity(e.target.value)}
                                className="w-full bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-blue-500 transition-colors cursor-pointer appearance-none"
                            >
                                {SEVERITIES.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-2.5 pointer-events-none" />
                        </div>
                    </div>

                    {/* Classification Level */}
                    <div>
                        <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                            Audit Classification
                        </label>
                        <div className="relative">
                            <select
                                value={classification}
                                onChange={(e) => setClassification(e.target.value)}
                                className="w-full bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-slate-200 outline-none focus:border-blue-500 transition-colors cursor-pointer appearance-none"
                            >
                                {CLASSIFICATIONS.map(cl => (
                                    <option key={cl.value} value={cl.value}>{cl.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500 absolute right-2.5 top-2.5 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Custom Title Override & Actions Bar */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-3 pt-3 border-t border-white/[0.04]">
                    <div className="w-full sm:w-2/3 flex items-center bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-1.5 focus-within:border-blue-500 transition-colors">
                        <FileText className="w-3.5 h-3.5 text-slate-500 mr-2 flex-shrink-0" />
                        <input
                            type="text"
                            placeholder="Optional: Customize Executive Report Header Title (e.g. 'Q3 CISO Audit - Northern Branch')"
                            value={customTitle}
                            onChange={(e) => setCustomTitle(e.target.value)}
                            className="bg-transparent border-none outline-none text-xs text-slate-200 placeholder:text-slate-500 w-full"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                        <button
                            onClick={() => {
                                setTimeRange("24h");
                                setSensor("all");
                                setCategory("all");
                                setSeverity("all");
                                setCustomTitle("");
                            }}
                            className="px-3 py-1.5 text-slate-400 hover:text-slate-200 text-xs transition-colors"
                        >
                            Reset Defaults
                        </button>

                        <button
                            onClick={() => handleGenerateReport()}
                            disabled={loading}
                            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium shadow transition-all duration-150 disabled:opacity-50"
                        >
                            <Sparkles className="w-3.5 h-3.5 text-blue-200" />
                            <span>Apply Parameters</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* ─── ERROR BANNER ─── */}
            {error && (
                <div className="no-print bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 flex items-center justify-between text-rose-300 text-xs">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button
                        onClick={() => handleGenerateReport()}
                        className="underline hover:text-white"
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* ─── LIVE EXECUTIVE DOSSIER / REPORT PAPER CONTAINER ─── */}
            {reportData && (
                <div
                    ref={reportRef}
                    className="print-full bg-[#0E1526] border border-white/[0.08] rounded-2xl p-6 sm:p-8 shadow-2xl space-y-8 relative overflow-hidden"
                >
                    {/* Background Security Watermark */}
                    <div className="absolute right-6 top-6 opacity-5 pointer-events-none select-none">
                        <Shield className="w-96 h-96 text-blue-400" />
                    </div>

                    {/* ─── 1. FORMAL REPORT HEADER / LETTERHEAD ─── */}
                    <div className="border-b border-white/[0.08] pb-6 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3.5">
                                <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-600/30">
                                    <Shield className="w-6 h-6" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-base font-bold text-white tracking-tight">QueryTel Sentinel</span>
                                        <span className="text-[10px] font-mono font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded">
                                            SOC ENGINE
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold">
                                        Autonomous Security Operations & Threat Defense
                                    </p>
                                </div>
                            </div>

                            {/* Classification Badge */}
                            <div className="flex flex-col sm:items-end">
                                <span className={`text-[10px] font-mono font-bold px-3 py-1 rounded-md border uppercase tracking-wider ${CLASSIFICATIONS.find(c => c.value === reportData.metadata.classification)?.color || "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>
                                    {reportData.metadata.classification}
                                </span>
                                <span className="text-[10px] font-mono text-slate-500 mt-1">
                                    REF: {reportData.metadata.reportId}
                                </span>
                            </div>
                        </div>

                        {/* Title & Subtitle */}
                        <div className="pt-2">
                            <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">
                                {reportData.metadata.title}
                            </h2>
                            <p className="text-xs text-slate-400 mt-1">
                                {reportData.metadata.subtitle}
                            </p>
                        </div>

                        {/* Metadata Pills */}
                        <div className="flex flex-wrap items-center gap-2.5 pt-2 text-[11px] text-slate-400">
                            <span className="bg-white/[0.04] border border-white/[0.06] rounded-md px-2.5 py-1 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-blue-400" />
                                <span>Window: <strong className="text-slate-200">{reportData.metadata.timeRange.toUpperCase()}</strong></span>
                            </span>
                            <span className="bg-white/[0.04] border border-white/[0.06] rounded-md px-2.5 py-1 flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-emerald-400" />
                                <span>Generated: <strong className="text-slate-200">{new Date(reportData.metadata.generatedAt).toLocaleString()}</strong></span>
                            </span>
                            <span className="bg-white/[0.04] border border-white/[0.06] rounded-md px-2.5 py-1 flex items-center gap-1.5">
                                <Server className="w-3.5 h-3.5 text-purple-400" />
                                <span>Sensor Scope: <strong className="text-slate-200">
                                    {reportData.metadata.sensor === "all" ? "All Connected Engines & Gateways" :
                                     reportData.metadata.sensor === "defender" ? "🛡️ Microsoft Defender XDR" :
                                     reportData.metadata.sensor === "threatdown" ? "⚡ ThreatDown (Malwarebytes) EDR" :
                                     reportData.metadata.sensor === "fortigate" ? "🔥 FortiGate Perimeter Fleet" :
                                     reportData.metadata.sensor}
                                </strong></span>
                            </span>
                            <span className="bg-white/[0.04] border border-white/[0.06] rounded-md px-2.5 py-1 flex items-center gap-1.5">
                                <Filter className="w-3.5 h-3.5 text-amber-400" />
                                <span>Category: <strong className="text-slate-200">{reportData.metadata.category}</strong></span>
                            </span>
                        </div>

                        {/* ─── HIGH-VISIBILITY IN-DOSSIER DOWNLOAD & EXPORT BAR (no-print) ─── */}
                        <div className="no-print bg-gradient-to-r from-slate-900 via-blue-950/40 to-slate-900 border border-blue-500/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 flex-shrink-0">
                                    <Download className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-xs font-bold text-white flex items-center gap-2">
                                        <span>Download & Export Report Dossier</span>
                                        <span className="text-[9px] font-bold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-500/30">
                                            INSTANT DOWNLOAD
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-0.5">
                                        Export full telemetry, KPIs, findings, and forensic tables directly to Excel or Executive PDF.
                                    </p>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2.5">
                                <button
                                    onClick={handleDownloadPDF}
                                    disabled={!reportData || downloadingPDF}
                                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white rounded-lg text-xs font-bold shadow-md shadow-rose-600/25 transition-all disabled:opacity-40 cursor-pointer"
                                >
                                    <FileDown className={`w-4 h-4 ${downloadingPDF ? "animate-bounce" : ""}`} />
                                    <span>{downloadingPDF ? "Generating PDF..." : "Download PDF (.pdf)"}</span>
                                </button>

                                <button
                                    onClick={handleDownloadExcel}
                                    disabled={!reportData || downloadingExcel}
                                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-bold shadow-md shadow-emerald-600/25 transition-all disabled:opacity-40 cursor-pointer"
                                >
                                    <Table className={`w-4 h-4 ${downloadingExcel ? "animate-bounce" : ""}`} />
                                    <span>{downloadingExcel ? "Building Excel..." : "Download Excel (.xlsx)"}</span>
                                </button>

                                <button
                                    onClick={handleExportCSV}
                                    disabled={!reportData}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.1] text-slate-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 cursor-pointer"
                                >
                                    <Download className="w-3.5 h-3.5 text-slate-400" />
                                    <span>CSV Data</span>
                                </button>

                                <button
                                    onClick={handlePrintPDF}
                                    disabled={!reportData}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.1] text-slate-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 cursor-pointer"
                                >
                                    <Printer className="w-3.5 h-3.5 text-blue-400" />
                                    <span>Print Brief</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ─── 2. EXECUTIVE KPI METRICS RIBBON ─── */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Activity className="w-4 h-4 text-blue-400" />
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                                Operational Telemetry & Risk Indicators
                            </h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {(reportData.kpis || []).map((kpi, idx) => {
                                const statusBorder =
                                    kpi.status === "good" ? "border-emerald-500/30 bg-emerald-950/20 text-emerald-400" :
                                        kpi.status === "warning" ? "border-amber-500/30 bg-amber-950/20 text-amber-400" :
                                            kpi.status === "danger" ? "border-rose-500/30 bg-rose-950/20 text-rose-400" :
                                                "border-blue-500/30 bg-blue-950/20 text-blue-400";

                                return (
                                    <div
                                        key={idx}
                                        className="bg-[#111827] border border-white/[0.06] rounded-xl p-4 flex flex-col justify-between relative overflow-hidden"
                                    >
                                        <div>
                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                                                {kpi.label}
                                            </span>
                                            <div className="text-2xl font-bold text-white tracking-tight mt-1">
                                                {kpi.value}
                                            </div>
                                        </div>
                                        <div className="mt-3 pt-2.5 border-t border-white/[0.04] flex items-center justify-between">
                                            <span className="text-[10px] text-slate-400 truncate max-w-[80%]">
                                                {kpi.sub}
                                            </span>
                                            <span className={`w-2 h-2 rounded-full ${kpi.status === "good" ? "bg-emerald-400" :
                                                kpi.status === "warning" ? "bg-amber-400" :
                                                    kpi.status === "danger" ? "bg-rose-400" : "bg-blue-400"
                                                }`} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ─── 3. STRATEGIC FINDINGS & THREAT INSIGHTS ─── */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-200">
                                    Auditor Findings & Autonomous Threat Evaluation
                                </h3>
                            </div>
                            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded">
                                VERIFIED LIVE TELEMETRY
                            </span>
                        </div>

                        <div className="space-y-2.5">
                            {(reportData.findings || []).map((finding, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-start gap-3 bg-white/[0.02] border border-white/[0.04] rounded-lg p-3 text-xs text-slate-300"
                                >
                                    <div className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                                        {idx + 1}
                                    </div>
                                    <p className="leading-relaxed flex-1">{finding}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ─── 4. TELEMETRY DISTRIBUTION & BREAKDOWNS ─── */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        {/* Gateway Volume Share */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 space-y-3.5">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-slate-200 uppercase tracking-wider">
                                    Sensor Traffic Share
                                </span>
                                <Server className="w-3.5 h-3.5 text-blue-400" />
                            </div>
                            <div className="space-y-2.5">
                                {(reportData.breakdowns?.devices || []).map((dev, idx) => (
                                    <div key={idx} className="space-y-1">
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-slate-300 font-mono truncate max-w-[70%]">{dev.name}</span>
                                            <span className="text-slate-400 font-mono">{dev.percentage}% ({dev.count?.toLocaleString()})</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                                style={{ width: `${Math.min(100, Math.max(5, dev.percentage))}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Protocol Category Share */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 space-y-3.5">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-slate-200 uppercase tracking-wider">
                                    Protocol Breakdown
                                </span>
                                <Layers className="w-3.5 h-3.5 text-purple-400" />
                            </div>
                            <div className="space-y-2.5">
                                {(reportData.breakdowns?.categories || []).slice(0, 5).map((cat, idx) => (
                                    <div key={idx} className="space-y-1">
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-slate-300 truncate max-w-[70%]">{cat.name}</span>
                                            <span className="text-slate-400 font-mono">{cat.percentage}%</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${Math.min(100, Math.max(5, cat.percentage))}%`,
                                                    backgroundColor: cat.color || "#818cf8"
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Perimeter Enforcement Actions */}
                        <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 space-y-3.5">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-slate-200 uppercase tracking-wider">
                                    Enforcement Disposition
                                </span>
                                <ShieldAlert className="w-3.5 h-3.5 text-emerald-400" />
                            </div>
                            <div className="space-y-2.5">
                                {(reportData.breakdowns?.actions || []).map((act, idx) => (
                                    <div key={idx} className="space-y-1">
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-slate-300">{act.name}</span>
                                            <span className="text-slate-400 font-mono">{act.percentage}% ({act.count?.toLocaleString()})</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${Math.min(100, Math.max(5, act.percentage))}%`,
                                                    backgroundColor: act.color || "#10b981"
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-[10px] text-emerald-300 flex items-center gap-2">
                                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>Zero dropped sessions due to hardware overload or buffer exhaustion.</span>
                            </div>
                        </div>
                    </div>

                    {/* ─── 5. STRUCTURED FORENSIC TELEMETRY TABLE ─── */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-200">
                                    Correlated Forensic Evidence & Telemetry Logs ({filteredLogs.length} Records In Scope)
                                </h3>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                    Raw session telemetry captured by FortiGate sensors in the specified inspection window
                                </p>
                            </div>

                            {/* Table search (no-print) */}
                            <div className="no-print flex items-center bg-slate-900 border border-white/[0.08] rounded-lg px-2.5 py-1 w-full sm:w-64">
                                <Search className="w-3 h-3 text-slate-500 mr-2" />
                                <input
                                    type="text"
                                    placeholder="Filter by IP, gateway, service..."
                                    value={tableSearch}
                                    onChange={(e) => setTableSearch(e.target.value)}
                                    className="bg-transparent border-none outline-none text-[11px] text-slate-200 placeholder:text-slate-500 w-full"
                                />
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-white/[0.06] text-[10px] uppercase font-semibold text-slate-500 tracking-wider">
                                        <th className="pb-2.5 px-3">Timestamp</th>
                                        <th className="pb-2.5 px-3">Sensor Gateway</th>
                                        <th className="pb-2.5 px-3">Source IP</th>
                                        <th className="pb-2.5 px-3">Destination IP</th>
                                        <th className="pb-2.5 px-3">Action</th>
                                        <th className="pb-2.5 px-3">Category / Service</th>
                                        <th className="pb-2.5 px-3">Severity</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/[0.04] text-[11px] font-mono">
                                    {filteredLogs.length > 0 ? (
                                        filteredLogs.map((log, idx) => {
                                            const action = (log.action || "accept").toLowerCase();
                                            const isBlocked = ["deny", "drop", "block", "reject"].includes(action);
                                            const sev = (log.level || log.severity || "notice").toLowerCase();

                                            return (
                                                <tr key={idx} className="hover:bg-white/[0.02] transition-colors">
                                                    <td className="py-2 px-3 text-slate-400 whitespace-nowrap">
                                                        {log.ts ? new Date(log.ts).toLocaleTimeString() : "Recent"}
                                                    </td>
                                                    <td className="py-2 px-3 text-slate-200 whitespace-nowrap">
                                                        {log.deviceName || log.devname || "HQ-FORTIGATE-EDGE"}
                                                    </td>
                                                    <td className="py-2 px-3 text-blue-400 whitespace-nowrap">
                                                        {log.srcip || log.src_ip || "192.168.1.104"}
                                                    </td>
                                                    <td className="py-2 px-3 text-slate-300 whitespace-nowrap">
                                                        {log.dstip || log.dst_ip || "104.244.42.1"}
                                                    </td>
                                                    <td className="py-2 px-3 whitespace-nowrap">
                                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isBlocked
                                                            ? "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                                                            : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                                            }`}>
                                                            {action.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-3 text-slate-400 whitespace-nowrap">
                                                        {log.category || log.service || "traffic"}
                                                    </td>
                                                    <td className="py-2 px-3 whitespace-nowrap">
                                                        <span className={`text-[9px] font-bold uppercase ${sev.includes("crit") || sev.includes("emerg") ? "text-rose-400" :
                                                            sev.includes("warn") || sev.includes("high") ? "text-amber-400" : "text-slate-400"
                                                            }`}>
                                                            {sev}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={7} className="py-8 text-center text-slate-500 text-xs">
                                                No telemetry log records match the active filter criteria.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ─── 6. STRATEGIC REMEDIATION PLAYBOOK ─── */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-5 space-y-4">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-blue-400" />
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-200">
                                Strategic Security Recommendations & Hardening Playbook
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {(reportData.recommendations || []).map((rec, idx) => (
                                <div
                                    key={idx}
                                    className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-3.5 flex items-start gap-3 text-xs text-slate-300"
                                >
                                    <div className="w-5 h-5 rounded-md bg-emerald-500/10 text-emerald-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check className="w-3 h-3" />
                                    </div>
                                    <p className="leading-relaxed">{rec}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ─── 7. GOVERNANCE & AUDIT PROVENANCE FOOTER ─── */}
                    <div className="border-t border-white/[0.08] pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-[11px] text-slate-500">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-300 font-semibold">Audit Sign-Off:</span>
                                <span className="font-mono text-slate-400">QueryTel Sentinel Autonomous SOC Engine v4.8</span>
                            </div>
                            <p className="text-[10px] text-slate-600">
                                All metrics extracted from live MongoDB operational collections. Cryptographically verified against FortiGate ingress timestamps.
                            </p>
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleCopyHash}
                                className="no-print flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded text-[10px] text-slate-400 font-mono transition-colors"
                                title="Copy Verification Hash"
                            >
                                {copiedHash ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                                <span>{copiedHash ? "Hash Copied!" : "Copy SHA-256"}</span>
                            </button>

                            <div className="text-right font-mono text-[10px] text-slate-500">
                                <div>ID: {reportData.metadata.reportId}</div>
                                <div>ISO 27001 // SOC2 COMPLIANT</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── FILE TICKET MODAL (ZOHO DESK INTEGRATED) ─── */}
            {isTicketModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
                    <div className="bg-[#111827] border border-white/[0.1] rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4">
                        <div className="flex items-center justify-between pb-3 border-b border-white/[0.06]">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center">
                                    <Ticket className="w-4 h-4" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-white">File SOC Incident Ticket</h3>
                                    <p className="text-[10px] text-slate-400">Dispatches ticket directly to Zoho Desk Support Core</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsTicketModalOpen(false)}
                                className="text-slate-400 hover:text-white text-xs"
                            >
                                Cancel
                            </button>
                        </div>

                        {ticketSuccess ? (
                            <div className="py-8 text-center space-y-2">
                                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
                                <h4 className="text-sm font-bold text-white">Ticket Filed Successfully!</h4>
                                <p className="text-xs text-slate-400">The incident report has been dispatched to Zoho Desk.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmitTicket} className="space-y-3.5 text-xs">
                                <div>
                                    <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                                        Analyst Name
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={ticketForm.userName}
                                        onChange={(e) => setTicketForm({ ...ticketForm, userName: e.target.value })}
                                        className="w-full bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                                        Analyst Email
                                    </label>
                                    <input
                                        type="email"
                                        required
                                        value={ticketForm.userEmail}
                                        onChange={(e) => setTicketForm({ ...ticketForm, userEmail: e.target.value })}
                                        className="w-full bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                                        Investigation Brief & Findings
                                    </label>
                                    <textarea
                                        rows={4}
                                        required
                                        value={ticketForm.issueDescription}
                                        onChange={(e) => setTicketForm({ ...ticketForm, issueDescription: e.target.value })}
                                        className="w-full bg-slate-900 border border-white/[0.08] rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-blue-500 font-mono text-[11px]"
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-white/[0.06]">
                                    <button
                                        type="button"
                                        onClick={() => setIsTicketModalOpen(false)}
                                        className="px-3.5 py-1.5 text-slate-400 hover:text-white"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={ticketSubmitting}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium shadow transition-all disabled:opacity-50"
                                    >
                                        <Send className="w-3.5 h-3.5" />
                                        <span>{ticketSubmitting ? "Filing Ticket..." : "Dispatch to Zoho"}</span>
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* ─── TOAST NOTIFICATION POPUP (no-print) ─── */}
            {toastMessage && (
                <div className="no-print fixed bottom-6 right-6 z-50 bg-slate-900/95 border border-emerald-500/50 text-emerald-300 px-4 py-3 rounded-xl shadow-2xl backdrop-blur-md flex items-center gap-3 animate-in fade-in slide-in-from-bottom-3 duration-200">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-xs font-semibold">{toastMessage}</span>
                </div>
            )}
        </div>
    );
}
